import { defineStore, storeToRefs } from 'pinia'
import { Ref, ref, computed } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { fetchSubscriptionClientReleases as fetchClientReleasesQuery } from '@/queries'
import { ClientRelease, ClientReleases, InstallerServiceResponse, ProductDownloadMap } from '@/types'

import { useProductStore } from '@/store/ProductStore'
import { sentryCapture } from '@/utils/sentry'

export const useDownloadStore = defineStore('DownloadStore', () => {
  const productStore = useProductStore()
  const { releasesForDownload } = storeToRefs(productStore)
  const clientReleases: Ref<ClientReleases> = ref({})

  const {
    load: fetchSubscriptionClientReleases,
    onResult: onSubscriptionClientReleases,
    loading: clientReleaseLoading,
    onError: onClientReleaseError
  } = useLazyQuery(fetchClientReleasesQuery)

  onSubscriptionClientReleases(releaseResult => {
    if (!releaseResult.data) {
      return
    }

    clientReleases.value = releaseResult.data
  })

  const fetchClientReleaseDownloads = () => {
    return fetchSubscriptionClientReleases(
      fetchClientReleasesQuery,
      {},
      {
        fetchPolicy: 'no-cache',
        context: {
          fetchOptions: {
            method: 'GET'
          }
        }
      }
    )
  }

  const productDownloads = computed(() => {
    return releasesForDownload.value.map((download: ProductDownloadMap) => {
      const { method, badge, name, id, value } = download

      const release = method === 'managed' ? reduceInstallerResult(value as InstallerServiceResponse) : clientReleases.value[value as keyof ClientReleases]

      return {
        name,
        id,
        badge,
        release
      }
    })
  })

  onClientReleaseError(err => sentryCapture(err))

  const reduceInstallerResult = (response: InstallerServiceResponse): ClientRelease => {
    const { mac, win, notes } = response
    return {
      mac,
      win,
      notes
    }
  }

  return {
    productDownloads,
    clientReleases,
    clientReleaseLoading,
    fetchClientReleaseDownloads
  }
})
